import { GetProductDetailsResponseType } from '@nw/shared/data-access';

import { createProductDetailModel } from '../factories/ProductDetailFactory';
import {
  DefaultProductDetail,
  EMIInstallmentProductDetail,
  FlexibleInstallmentProductDetail,
  FlexibleTenureEMIProductDetail,
  PeriodicInstallmentProductDetail
} from '../models';

/**
 * Fee amount for NIAT application sections (Application Fee, Enrollment) that use
 * {@link ApplicationSectionProductModel} instead of the full payment-core machine.
 *
 * Delegates product typing to {@link createProductDetailModel} so **change-program** payloads
 * and **installment** shapes follow the same rules as {@link FlexibleInstallmentPricingDetails}
 * (principal from `customizable_installment_details`), {@link PeriodicInstallmentPricingDetails},
 * etc. — not only `pricing_details.total_amount_to_be_paid`, which is often null for customizable
 * installment + change program responses.
 */
export function resolveApplicationPortalProductFee(
  productData: GetProductDetailsResponseType
): number {
  const fallbackOriginal = productData.original_amount ?? 0;

  try {
    const model = createProductDetailModel(productData, null);

    if (model instanceof FlexibleInstallmentProductDetail) {
      const { currentPricingDetails: d } = model;
      if (d.totalPrincipalAmountToBePaid > 0) {
        return d.totalPrincipalAmountToBePaid;
      }
      if (d.totalAmountToBePaid > 0) {
        return d.totalAmountToBePaid;
      }
      return fallbackOriginal;
    }

    if (model instanceof PeriodicInstallmentProductDetail) {
      const { currentPricingDetails: d } = model;
      if (d.principalAmountToBePaid > 0) {
        return d.principalAmountToBePaid;
      }
      if (d.totalAmountToBePaid > 0) {
        return d.totalAmountToBePaid;
      }
      return fallbackOriginal;
    }

    if (
      model instanceof EMIInstallmentProductDetail ||
      model instanceof FlexibleTenureEMIProductDetail
    ) {
      const { currentPricingDetails: d } = model;
      if (d.totalAmountToBePaid > 0) {
        return d.totalAmountToBePaid;
      }
      if (d.amountToBePaid > 0) {
        return d.amountToBePaid;
      }
      return fallbackOriginal;
    }

    if (model instanceof DefaultProductDetail) {
      const { currentPricingDetails: d } = model;
      const direct = d.amountToBePaid;
      if (direct != null && !Number.isNaN(Number(direct)) && Number(direct) > 0) {
        return Number(direct);
      }
      const fromBase = model.getTotalAmountToBePaid();
      if (fromBase > 0) {
        return fromBase;
      }
      return fallbackOriginal;
    }
  } catch {
    // Unknown pricing_type or missing installment block — use API fallbacks below
  }

  const pricing =
    productData.product_purchase_details_for_user?.pricing_details;
  const total = pricing?.total_amount_to_be_paid;
  if (total != null && !Number.isNaN(Number(total)) && Number(total) > 0) {
    return Number(total);
  }

  const customizablePrincipal =
    pricing?.customizable_installment_details?.total_principal_amount_to_be_paid;
  if (
    customizablePrincipal != null &&
    !Number.isNaN(Number(customizablePrincipal)) &&
    Number(customizablePrincipal) > 0
  ) {
    return Number(customizablePrincipal);
  }

  const periodicPrincipal =
    pricing?.periodic_installment_details?.principal_amount_to_be_paid;
  if (
    periodicPrincipal != null &&
    !Number.isNaN(Number(periodicPrincipal)) &&
    Number(periodicPrincipal) > 0
  ) {
    return Number(periodicPrincipal);
  }

  return fallbackOriginal;
}
